import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import { Main as Container } from 'Components/Main';
import { PageHelper } from '../../Components/PageHelper';
import { useSelector } from 'react-redux';
import { useAuthTenant } from 'store/auth';
import { deleteUsersGroup, getUsersGroupsPaged } from 'services/users-groups-service';
import { Dialog, PageHeader, Select, TextField, Button, EditSvg } from 'Components/ui';
import { useTranslation } from 'react-i18next';
import styled, { keyframes } from 'styled-components';

// ─── Icons ────────────────────────────────────────────────────────────────────

const InfoSvg = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--color-brand)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 1 }}>
    <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
  </svg>
);

const SearchSvg = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
  </svg>
);

const EyeSvg = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
  </svg>
);


const TrashSvg = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6"/>
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
  </svg>
);

const PlusSvg = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
  </svg>
);

// ─── Styled components ────────────────────────────────────────────────────────

const PageWrap = styled.div`
  padding-top: 16px;
  padding-bottom: 40px;
`;

const InfoBanner = styled.div`
  background: white;
  border-radius: 14px;
  padding: 16px 20px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
  border-left: 4px solid var(--color-brand);
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 16px;
`;

const InfoTextWrap = styled.div``;

const InfoTitle = styled.p`
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: var(--vo-ink);
  margin: 0 0 2px;
`;

const InfoDesc = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: var(--vo-slate);
  margin: 0;
  line-height: 1.55;
`;

const FilterBar = styled.div`
  background: white;
  border-radius: 14px;
  padding: 20px 24px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
  margin-bottom: 16px;
  display: flex;
  gap: 14px;
  align-items: flex-end;
`;

const FilterField = styled.div`
  flex: ${({ $flex }) => $flex ?? 1};
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const FilterLabel = styled.label`
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: var(--vo-slate);
`;

const FilterBtn = styled.button`
  height: 40px;
  background: var(--color-brand);
  color: white;
  border: none;
  border-radius: 10px;
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  padding: 0 20px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 7px;
  transition: background 0.15s;

  &:hover { background: var(--color-brand-strong); }
`;

const TableCard = styled.div`
  background: white;
  border-radius: 14px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
  overflow: hidden;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
`;

const Thead = styled.thead`
  background: var(--vo-mist);
`;

const Th = styled.th`
  font-family: 'Maven Pro', sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: var(--vo-slate);
  text-transform: uppercase;
  letter-spacing: 0.08em;
  padding: 12px 16px;
  text-align: ${({ $center }) => $center ? 'center' : 'left'};
  border-bottom: 1px solid var(--vo-cloud);
  width: ${({ $width }) => $width ?? 'auto'};
`;

const Tr = styled.tr`
  border-bottom: 1px solid var(--vo-mist);
  transition: background 0.1s;

  &:hover { background: var(--vo-capri-25); }
  &:last-child { border-bottom: none; }
`;

const TdName = styled.td`
  padding: 13px 16px;
  font-family: 'Lato', sans-serif;
  font-size: 14px;
  color: var(--vo-ink);
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const TdMuted = styled.td`
  padding: 13px 16px;
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: var(--vo-slate);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const TdStatus = styled.td`
  padding: 13px 16px;
`;

const Badge = styled.span`
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: ${({ $active }) => $active ? 'var(--vo-success)' : 'var(--vo-slate)'};
  background: ${({ $active }) => $active ? 'var(--vo-success-10)' : 'var(--vo-slate-10)'};
  padding: 3px 10px;
  border-radius: 20px;
  display: inline-block;
`;

const TdActions = styled.td`
  padding: 13px 16px;
  text-align: center;
`;

const ActionsGroup = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2px;
`;


const EmptyCell = styled.td`
  padding: 40px 16px;
  text-align: center;
  font-family: 'Lato', sans-serif;
  font-size: 14px;
  color: var(--vo-slate);
`;

const shimmer = keyframes`
  0% { background-position: -400px 0; }
  100% { background-position: 400px 0; }
`;

const SkeletonBlock = styled.div`
  background: linear-gradient(90deg, var(--vo-mist) 25%, var(--vo-cloud) 50%, var(--vo-mist) 75%);
  background-size: 800px 100%;
  animation: ${shimmer} 1.4s infinite linear;
  border-radius: 14px;
`;

// ─── Component ────────────────────────────────────────────────────────────────

export const UsersGroups = () => {
  const { t } = useTranslation(['user']);
  const navigate = useNavigate();
  const selectedTenant = useSelector(useAuthTenant);

  const [nameInput, setNameInput] = useState('');
  const [statusInput, setStatusInput] = useState('');
  const [nameFilter, setNameFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [refetch, setRefetch] = useState(false);
  const [page] = useState(1);

  const { isLoading, error, data: pagedGroups } = useQuery(
    ['getUsersGroups', refetch, page],
    async () => {
      if (!selectedTenant?.slug) return { data: [], page: 1, totalPages: 0 };
      const result = await getUsersGroupsPaged(selectedTenant.slug, page, 15);
      if (result?.data?.data?.length > 0) return result.data;
      return { data: [], page, totalPages: 0 };
    }
  );

  const filteredData = (pagedGroups?.data ?? []).filter(g => {
    const nameMatch = !nameFilter || g.name?.toLowerCase().includes(nameFilter.toLowerCase());
    const statusMatch = !statusFilter || statusFilter === 'all' ||
      (statusFilter === 'active' && g.active) ||
      (statusFilter === 'inactive' && !g.active);
    return nameMatch && statusMatch;
  });

  const onDelete = async (group) => {
    const confirmed = await Dialog.confirm({
      title: t('user:groups.delete.confirm', { name: group.name }),
      confirmColor: 'var(--vo-danger)',
      confirmLabel: 'Excluir',
      cancelLabel: 'Cancelar',
    });
    if (!confirmed) return;
    try {
      const res = await deleteUsersGroup(group._id);
      if (res.status === 204) {
        setRefetch(v => !v);
        Dialog.alert({ title: t('user:groups.delete.successTitle'), message: t('user:groups.success.deleted'), icon: 'success' });
      } else throw new Error();
    } catch {
      Dialog.alert({ title: t('user:groups.delete.errorTitle'), icon: 'error' });
    }
  };

  const tourSteps = [
    { target: 'body', content: 'Bem-vindo à página de Grupos de Usuários! Aqui você organiza usuários em grupos para facilitar o envio de políticas.', placement: 'center', disableBeacon: true },
    { target: '[data-tour="info-section"]', content: 'Esta seção explica o propósito dos grupos de usuários.', placement: 'bottom', title: 'Informações' },
    { target: '[data-tour="add-button"]', content: 'Clique aqui para criar um novo grupo de usuários.', placement: 'bottom', title: 'Adicionar Grupo' },
    { target: '[data-tour="groups-table"]', content: 'Aqui são listados todos os grupos cadastrados com nome, contagem de usuários e status.', placement: 'top', title: 'Lista de Grupos' },
  ];

  return (
    <Container>
      <PageHelper steps={tourSteps} tourKey="users-groups-tour" autoStart={true} showButton={true} />
      <PageWrap>

        {/* ── Header ─────────────────────────────────────────────── */}
        <PageHeader
          data-tour="page-title"
          title={t('user:groups.title')}
          segments={[
            { label: t('common:breadcrumbs.manage') },
            { label: t('user:groups.title') },
          ]}
          actions={
            <Button
              variant="primary"
              data-tour="add-button"
              onClick={() => navigate('/users-groups/add')}
              startIcon={<PlusSvg />}
              sx={{ height: '44px', padding: '0 20px' }}
            >
              {t('user:groups.addNew')}
            </Button>
          }
        />

        {/* ── Info banner ────────────────────────────────────────── */}
        <InfoBanner data-tour="info-section">
          <InfoSvg />
          <InfoTextWrap>
            <InfoTitle>{t('user:groups.infoTitle')}</InfoTitle>
            <InfoDesc>{t('user:groups.description')}</InfoDesc>
          </InfoTextWrap>
        </InfoBanner>

        {/* ── Filter bar ─────────────────────────────────────────── */}
        <FilterBar>
          <FilterField $flex={2}>
            <FilterLabel>{t('user:groups.table.name')}</FilterLabel>
            <TextField
              fullWidth
              value={nameInput}
              onChange={e => setNameInput(e.target.value)}
              placeholder={t('user:groups.filter.namePlaceholder')}
              onKeyDown={e => e.key === 'Enter' && (setNameFilter(nameInput), setStatusFilter(statusInput))}
            />
          </FilterField>
          <FilterField $flex={1}>
            <FilterLabel>{t('user:groups.filter.status')}</FilterLabel>
            <Select
              name="statusInput"
              value={statusInput}
              onChange={setStatusInput}
              options={[
                { value: '', label: t('user:groups.filter.selectStatus') },
                { value: 'all', label: t('user:groups.filter.statusAll') },
                { value: 'active', label: t('user:groups.filter.statusActive') },
                { value: 'inactive', label: t('user:groups.filter.statusInactive') },
              ]}
            />
          </FilterField>
          <FilterBtn onClick={() => { setNameFilter(nameInput); setStatusFilter(statusInput); }}>
            <SearchSvg />
            {t('user:groups.filter.search')}
          </FilterBtn>
        </FilterBar>

        {/* ── Loading ────────────────────────────────────────────── */}
        {isLoading && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <SkeletonBlock style={{ height: '48px' }} />
            <SkeletonBlock style={{ height: '260px' }} />
          </div>
        )}

        {/* ── Error ──────────────────────────────────────────────── */}
        {error && <InfoDesc style={{ color: 'var(--vo-danger)' }}>{t('user:groups.error')}</InfoDesc>}

        {/* ── Table ──────────────────────────────────────────────── */}
        {!isLoading && !error && (
          <TableCard data-tour="groups-table">
            <Table>
              <Thead>
                <tr>
                  <Th $width="35%">{t('user:groups.table.name')}</Th>
                  <Th $width="25%">{t('user:groups.table.users')}</Th>
                  <Th $width="25%">{t('user:groups.table.status')}</Th>
                  <Th $width="15%" $center>{t('user:groups.table.actions')}</Th>
                </tr>
              </Thead>
              <tbody>
                {filteredData.length === 0 ? (
                  <tr>
                    <EmptyCell colSpan={4}>{t('user:groups.noData')}</EmptyCell>
                  </tr>
                ) : (
                  filteredData.map(g => (
                    <Tr key={g._id}>
                      <TdName>{g.name}</TdName>
                      <TdMuted>
                        {(() => {
                          const count = g.users?.length ?? g.userCount ?? 0;
                          const word = count === 1
                            ? t('user:groups.table.userSingular')
                            : t('user:groups.table.userPlural');
                          return `${count} ${word}`;
                        })()}
                      </TdMuted>
                      <TdStatus>
                        <Badge $active={g.active}>
                          {g.active ? t('user:groups.badge.active') : t('user:groups.badge.inactive')}
                        </Badge>
                      </TdStatus>
                      <TdActions>
                        <ActionsGroup>
                          <Button iconOnly variant="icon" title={t('user:groups.table.actions')} onClick={() => navigate(`/users-groups/show/${g._id}`)}>
                            <EyeSvg />
                          </Button>
                          <Button iconOnly variant="icon" title={t('common:tooltips.edit')} onClick={() => navigate(`/users-groups/edit/${g._id}`)}>
                            <EditSvg />
                          </Button>
                          <Button iconOnly variant="icon" danger title={t('common:tooltips.delete')} onClick={() => onDelete(g)}>
                            <TrashSvg />
                          </Button>
                        </ActionsGroup>
                      </TdActions>
                    </Tr>
                  ))
                )}
              </tbody>
            </Table>
          </TableCard>
        )}

      </PageWrap>
    </Container>
  );
};
